import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import {
  ArrowLeft, Send, MessageSquare, X, AtSign, Hash, Users, Paperclip, Download,
  MoreHorizontal, Reply, Pencil, Trash2, Check, FileText, Film, Image as ImageIcon, Music,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import Loader from '@/components/Loader';
import ConfirmDialog from '@/components/ConfirmDialog';
import { projectService, taskService, fileUrl } from '@/services';
import { useAuth } from '@/hooks/useAuth';
import { fmtDateTime } from '@/utils/format';

const LEGACY_MENTION_RE = /@\[([^\]:]+):([a-f0-9]{24})\]/gi;
const LEGACY_TASK_RE = /#\[([^\]:]+):([a-f0-9]{24})\]/gi;

function normalizeText(text = '') {
  return text.replace(LEGACY_MENTION_RE, '@$1').replace(LEGACY_TASK_RE, '#$1');
}

function escapeHtml(s = '') {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildInputHTML(text, pickedUsers, pickedTasks) {
  // Important: no padding/margin on these spans — they must occupy the exact same
  // horizontal width as the underlying textarea text, otherwise the caret drifts.
  const needles = [
    ...pickedUsers.map((u) => ({ str: `@${u.name}`, cls: 'rounded bg-brand-100 text-brand-700 dark:bg-brand-500/25 dark:text-brand-300' })),
    ...pickedTasks.map((t) => ({ str: `#${t.title}`, cls: 'rounded bg-violet-100 text-violet-700 dark:bg-violet-500/25 dark:text-violet-300' })),
  ].sort((a, b) => b.str.length - a.str.length);
  const claimed = new Array(text.length).fill(false);
  const matches = [];
  for (const n of needles) {
    let i = 0;
    while ((i = text.indexOf(n.str, i)) !== -1) {
      let conflict = false;
      for (let k = i; k < i + n.str.length; k++) if (claimed[k]) { conflict = true; break; }
      if (!conflict) {
        for (let k = i; k < i + n.str.length; k++) claimed[k] = true;
        matches.push({ idx: i, len: n.str.length, cls: n.cls });
      }
      i += n.str.length;
    }
  }
  matches.sort((a, b) => a.idx - b.idx);
  let html = '';
  let cursor = 0;
  for (const m of matches) {
    if (m.idx > cursor) html += escapeHtml(text.slice(cursor, m.idx));
    html += `<span class="${m.cls}">${escapeHtml(text.slice(m.idx, m.idx + m.len))}</span>`;
    cursor = m.idx + m.len;
  }
  if (cursor < text.length) html += escapeHtml(text.slice(cursor));
  if (text.endsWith('\n')) html += '&nbsp;';
  return html;
}

function resolveIds(text, picks) {
  if (!picks || picks.length === 0) return [];
  const sorted = [...picks].sort((a, b) => b.str.length - a.str.length);
  const claimed = new Array(text.length).fill(false);
  const found = new Set();
  for (const p of sorted) {
    let i = 0;
    while ((i = text.indexOf(p.str, i)) !== -1) {
      let conflict = false;
      for (let k = i; k < i + p.str.length; k++) if (claimed[k]) { conflict = true; break; }
      if (!conflict) {
        for (let k = i; k < i + p.str.length; k++) claimed[k] = true;
        found.add(p.id);
      }
      i += p.str.length;
    }
  }
  return Array.from(found);
}

function bytesToHuman(n = 0) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function classifyFile(file) {
  const m = (file.type || '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  return 'file';
}

// Match http(s) and bare www. URLs, stopping at whitespace or angle brackets.
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>]+/gi;

// Push text into `out` as nodes, turning detected URLs into <a> tags.
function pushLinkified(out, str, keyPrefix, linkClass) {
  if (!str) return;
  const re = new RegExp(URL_RE.source, URL_RE.flags);
  let lastIndex = 0;
  let match;
  let i = 0;
  while ((match = re.exec(str)) !== null) {
    if (match.index > lastIndex) {
      out.push(<span key={`${keyPrefix}-${i++}`}>{str.slice(lastIndex, match.index)}</span>);
    }
    // Trim trailing punctuation that's usually not part of the URL.
    let url = match[0];
    const trail = url.match(/[.,!?;:)\]}'"]+$/);
    if (trail) url = url.slice(0, url.length - trail[0].length);
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    out.push(
      <a
        key={`${keyPrefix}-${i++}`}
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className={linkClass}
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>
    );
    if (trail) {
      out.push(<span key={`${keyPrefix}-${i++}`}>{trail[0]}</span>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < str.length) {
    out.push(<span key={`${keyPrefix}-${i++}`}>{str.slice(lastIndex)}</span>);
  }
}

export default function ProjectChat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();

  const project = useQuery({ queryKey: ['project', id], queryFn: () => projectService.get(id) });
  const tasks = useQuery({
    queryKey: ['tasks', 'by-project', id, 'chat'],
    queryFn: () => taskService.list({ project: id, limit: 200 }),
    enabled: !!id,
  });
  const messages = useQuery({
    queryKey: ['project-chat', id],
    queryFn: () => projectService.listMessages(id, { limit: 200 }),
    refetchInterval: 2000,
    enabled: !!id,
  });

  const p = project.data?.project;
  const memberList = p?.members || [];
  const taskList = tasks.data?.items || [];
  const msgItems = messages.data?.items || [];

  // ===== Input state =====
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [trigger, setTrigger] = useState({ type: null, query: '', start: -1 });
  const [highlight, setHighlight] = useState(0);
  const [pickedUsers, setPickedUsers] = useState([]);
  const [pickedTasks, setPickedTasks] = useState([]);
  const [files, setFiles] = useState([]); // [{ file, previewUrl, kind }]
  const [replyTo, setReplyTo] = useState(null); // message being replied to
  const [editingId, setEditingId] = useState(null); // message ID being edited
  const [lightbox, setLightbox] = useState(null); // { items: [...attachments], index }
  const [confirmState, setConfirmState] = useState(null);

  const taRef = useRef(null);
  const ghostRef = useRef(null);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  // ===== Trigger detection (@ / #) =====
  const detectTrigger = (value, cursor) => {
    const before = value.slice(0, cursor);
    const atIdx = before.lastIndexOf('@');
    const hashIdx = before.lastIndexOf('#');
    const idx = Math.max(atIdx, hashIdx);
    if (idx < 0) return { type: null, query: '', start: -1 };
    const between = before.slice(idx + 1);
    if (between.startsWith('[')) return { type: null, query: '', start: -1 };
    const charBefore = idx === 0 ? ' ' : before[idx - 1];
    if (charBefore && /\w/.test(charBefore)) return { type: null, query: '', start: -1 };
    if (/\s/.test(between) || between.length > 30) return { type: null, query: '', start: -1 };
    const type = idx === atIdx ? 'user' : 'task';
    return { type, query: between.toLowerCase(), start: idx };
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    const cursor = e.target.selectionStart || 0;
    setTrigger(detectTrigger(value, cursor));
    setHighlight(0);
  };

  const handleKeyUp = (e) => {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
      const cursor = e.target.selectionStart || 0;
      setTrigger(detectTrigger(text, cursor));
    }
  };

  const closeTrigger = () => setTrigger({ type: null, query: '', start: -1 });

  const insertItem = (item) => {
    const start = trigger.start;
    if (start < 0) return;
    const before = text.slice(0, start);
    const cursor = taRef.current?.selectionStart ?? text.length;
    const after = text.slice(cursor);
    let display;
    if (trigger.type === 'user') {
      display = `@${item.name}`;
      setPickedUsers((arr) => (arr.some((u) => u.id === item._id) ? arr : [...arr, { id: item._id, name: item.name }]));
    } else {
      display = `#${item.title}`;
      setPickedTasks((arr) => (arr.some((t) => t.id === item._id) ? arr : [...arr, { id: item._id, title: item.title }]));
    }
    const next = `${before}${display} ${after}`;
    setText(next);
    closeTrigger();
    requestAnimationFrame(() => {
      taRef.current?.focus();
      const pos = (before + display + ' ').length;
      taRef.current?.setSelectionRange(pos, pos);
    });
  };

  const insertTriggerChar = (ch) => {
    const ta = taRef.current;
    const cursor = ta?.selectionStart ?? text.length;
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const needsSpace = before.length > 0 && !/\s$/.test(before);
    const next = `${before}${needsSpace ? ' ' : ''}${ch}${after}`;
    setText(next);
    requestAnimationFrame(() => {
      ta?.focus();
      const pos = (before + (needsSpace ? ' ' : '') + ch).length;
      ta?.setSelectionRange(pos, pos);
      setTrigger(detectTrigger(next, pos));
      setHighlight(0);
    });
  };

  // ===== File selection =====
  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const next = list.map((file) => ({
      file,
      kind: classifyFile(file),
      previewUrl: file.type.startsWith('image/') || file.type.startsWith('video/')
        ? URL.createObjectURL(file)
        : null,
    }));
    setFiles((arr) => [...arr, ...next]);
    e.target.value = ''; // reset so picking same file again works
  };

  const removeFile = (index) => {
    setFiles((arr) => {
      const removed = arr[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return arr.filter((_, i) => i !== index);
    });
  };

  useEffect(() => () => {
    // Cleanup preview URLs on unmount
    files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Send / Edit =====
  const send = async () => {
    const value = text.trim();
    if ((!value && files.length === 0) || sending) return;

    if (editingId) {
      setSending(true);
      try {
        await projectService.editMessage(id, editingId, value);
        setEditingId(null);
        setText('');
        setPickedUsers([]);
        setPickedTasks([]);
        await messages.refetch();
      } catch (e) {
        toast.error(e.response?.data?.message || 'Failed to update');
      } finally {
        setSending(false);
      }
      return;
    }

    const mentions = resolveIds(value, pickedUsers.map((u) => ({ id: u.id, str: `@${u.name}` })));
    const taskRefs = resolveIds(value, pickedTasks.map((t) => ({ id: t.id, str: `#${t.title}` })));

    const fd = new FormData();
    fd.append('text', value);
    mentions.forEach((m) => fd.append('mentions', m));
    taskRefs.forEach((tr) => fd.append('taskRefs', tr));
    if (replyTo) fd.append('replyTo', replyTo._id);
    files.forEach((f) => fd.append('attachments', f.file));

    setSending(true);
    try {
      await projectService.sendMessage(id, fd);
      // Cleanup
      files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
      setFiles([]);
      setText('');
      setPickedUsers([]);
      setPickedTasks([]);
      setReplyTo(null);
      const fresh = await messages.refetch();
      qc.setQueryData(['project-chat', id], fresh.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const startEdit = (msg) => {
    setEditingId(msg._id);
    setText(normalizeText(msg.text || ''));
    setReplyTo(null);
    requestAnimationFrame(() => taRef.current?.focus());
  };

  const cancelEdit = () => {
    setEditingId(null);
    setText('');
  };

  const startReply = (msg) => {
    setReplyTo(msg);
    setEditingId(null);
    requestAnimationFrame(() => taRef.current?.focus());
  };

  const remove = (messageId) => {
    setConfirmState({
      title: 'Delete message',
      message: 'This message will be permanently removed from the chat. Continue?',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await projectService.deleteMessage(id, messageId);
          messages.refetch();
        } catch (e) {
          toast.error(e.response?.data?.message || 'Failed to delete');
        }
      },
    });
  };

  // ===== Candidates =====
  const candidates = useMemo(() => {
    const q = trigger.query;
    if (trigger.type === 'user') return memberList.filter((m) => !q || m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)).slice(0, 8);
    if (trigger.type === 'task') return taskList.filter((t) => !q || t.title.toLowerCase().includes(q)).slice(0, 8);
    return [];
  }, [trigger, memberList, taskList]);

  const onKeyDown = (e) => {
    if (trigger.type && candidates.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % candidates.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + candidates.length) % candidates.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertItem(candidates[highlight]); return; }
      if (e.key === 'Escape') { e.preventDefault(); closeTrigger(); return; }
    }
    if (e.key === 'Escape' && (editingId || replyTo)) {
      e.preventDefault();
      if (editingId) cancelEdit();
      if (replyTo) setReplyTo(null);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [msgItems.length]);

  const grouped = useMemo(() => {
    const groups = [];
    let current = null;
    for (const m of msgItems) {
      const day = dayjs(m.createdAt).format('YYYY-MM-DD');
      if (!current || current.day !== day) { current = { day, items: [] }; groups.push(current); }
      current.items.push(m);
    }
    return groups;
  }, [msgItems]);

  const isForbidden = messages.error?.response?.status === 403;
  const inputHTML = useMemo(() => buildInputHTML(text, pickedUsers, pickedTasks), [text, pickedUsers, pickedTasks]);

  const openLightbox = (msg, index) => {
    const items = (msg.attachments || []).filter((a) => a.kind === 'image' || a.kind === 'video');
    if (!items.length) return;
    setLightbox({ items, index });
  };

  if (project.isLoading) return <Loader />;
  if (!p) return <div>Project not found</div>;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] -m-4 md:-m-6">
      {/* Header */}
      <div className="px-4 md:px-6 py-3 bg-gradient-to-r from-white to-slate-50 dark:from-slate-900 dark:to-slate-900/60 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(`/projects/${p._id}`)} className="btn-ghost p-2 rounded-full" title="Back to project">
            <ArrowLeft size={18} />
          </button>
          <div className="relative shrink-0">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-brand-500 to-indigo-600 text-white grid place-items-center font-bold text-lg shadow-sm">
              {p.name?.[0]?.toUpperCase()}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500 border-2 border-white dark:border-slate-900" />
            </span>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-lg truncate">{p.name}</h2>
              <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                <MessageSquare size={10} /> Chat
              </span>
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
              <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
              </span>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <span className="flex items-center gap-1"><Users size={12} /> {memberList.length} member{memberList.length === 1 ? '' : 's'}</span>
            </div>
          </div>
        </div>
        {memberList.length > 0 && (
          <div className="hidden md:flex items-center -space-x-2 pr-1">
            {memberList.slice(0, 5).map((m) => (
              <div key={m._id} title={m.name} className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center text-xs font-semibold ring-2 ring-white dark:ring-slate-900">
                {m.name?.[0]?.toUpperCase()}
              </div>
            ))}
            {memberList.length > 5 && (
              <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 grid place-items-center text-xs font-semibold ring-2 ring-white dark:ring-slate-900">
                +{memberList.length - 5}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4 bg-slate-50 dark:bg-slate-950 space-y-3">
        {messages.isLoading && <Loader />}
        {isForbidden && (
          <div className="text-center text-sm text-slate-500 py-12">You aren't a member of this project, so the chat is hidden.</div>
        )}
        {!messages.isLoading && !isForbidden && msgItems.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-16">No messages yet. Say hi to your team 👋</div>
        )}
        {grouped.map((g) => (
          <div key={g.day} className="space-y-2">
            <div className="flex items-center justify-center my-3">
              <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                {dayjs(g.day).isSame(dayjs(), 'day') ? 'Today' : dayjs(g.day).format('DD MMM YYYY')}
              </span>
            </div>
            {g.items.map((m) => (
              <ChatBubble
                key={m._id}
                msg={m}
                own={m.sender?._id === user?._id}
                currentUserId={user?._id}
                onDelete={() => remove(m._id)}
                onEdit={() => startEdit(m)}
                onReply={() => startReply(m)}
                onOpenLightbox={(idx) => openLightbox(m, idx)}
                navigate={navigate}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Input area */}
      {!isForbidden && (
        <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
          {/* Reply / Edit banner */}
          {replyTo && <ReplyBanner msg={replyTo} onClear={() => setReplyTo(null)} />}
          {editingId && (
            <div className="px-4 md:px-6 pt-3">
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-xs">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                  <Pencil size={12} /> Editing message
                </div>
                <button type="button" onClick={cancelEdit} className="text-amber-700 dark:text-amber-300 hover:underline">Cancel</button>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="px-4 md:px-6 pt-3 flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => insertTriggerChar('#')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">
              <Hash size={12} /> Mention task
            </button>
            <button type="button" onClick={() => insertTriggerChar('@')}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">
              <AtSign size={12} /> Mention someone
            </button>
            <span className="text-[11px] text-slate-400 ml-auto hidden sm:inline">
              <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">@</kbd> people · <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">#</kbd> tasks · <kbd className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">Enter</kbd> send
            </span>
          </div>

          {/* Selected files preview row */}
          {files.length > 0 && (
            <div className="px-4 md:px-6 pt-3 flex gap-2 flex-wrap">
              {files.map((f, i) => (
                <FilePreview key={i} entry={f} onRemove={() => removeFile(i)} />
              ))}
            </div>
          )}

          {/* Trigger popover */}
          <div className="relative">
            {trigger.type && candidates.length > 0 && (
              <div className="absolute bottom-full left-4 md:left-6 mb-2 w-80 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 shadow-soft overflow-hidden z-10">
                <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-800 flex items-center gap-1">
                  {trigger.type === 'user' ? <><AtSign size={11} /> Mention member</> : <><Hash size={11} /> Mention task</>}
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {candidates.map((c, i) => (
                    <button key={c._id} type="button"
                      onMouseDown={(e) => { e.preventDefault(); insertItem(c); }}
                      onMouseEnter={() => setHighlight(i)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${i === highlight ? 'bg-brand-50 dark:bg-brand-500/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'}`}>
                      {trigger.type === 'user' ? (
                        <>
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center text-xs font-semibold">{c.name?.[0]?.toUpperCase()}</div>
                          <div className="min-w-0">
                            <div className="font-medium truncate">{c.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <Hash size={14} className="text-violet-500 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">{c.title}</div>
                            <div className="text-[11px] text-slate-500 capitalize">{c.status}</div>
                          </div>
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="px-4 md:px-6 pb-3 pt-2">
              <div className="relative">
                <div
                  ref={ghostRef}
                  aria-hidden="true"
                  className="input absolute inset-0 min-h-[6rem] max-h-60 overflow-auto pointer-events-none whitespace-pre-wrap break-words bg-transparent border-transparent text-slate-800 dark:text-slate-100 pr-14"
                  dangerouslySetInnerHTML={{ __html: inputHTML || '&#8203;' }}
                />
                <textarea
                  ref={taRef}
                  value={text}
                  onChange={handleChange}
                  onKeyDown={onKeyDown}
                  onKeyUp={handleKeyUp}
                  onClick={(e) => setTrigger(detectTrigger(text, e.target.selectionStart || 0))}
                  onScroll={(e) => { if (ghostRef.current) ghostRef.current.scrollTop = e.target.scrollTop; }}
                  rows={3}
                  placeholder={editingId ? 'Edit your message...' : 'Write a message... (@ for people, # for tasks, Enter to send)'}
                  className="input resize-none w-full min-h-[6rem] max-h-60 relative bg-transparent text-transparent caret-slate-800 dark:caret-slate-100 selection:bg-brand-200/60 dark:selection:bg-brand-500/30 pr-14"
                />

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.7z,.csv,.json,.md"
                  onChange={onPickFiles}
                  className="hidden"
                />

                {/* Stacked action buttons inside the input */}
                <div className="absolute bottom-2.5 right-2.5 flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach images, videos or files"
                    aria-label="Attach files"
                    disabled={editingId !== null}
                    className="w-10 h-10 rounded-xl grid place-items-center transition-all bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Paperclip size={16} />
                  </button>
                  <button
                    onClick={send}
                    disabled={sending || (!text.trim() && files.length === 0)}
                    title={editingId ? 'Save (Enter)' : 'Send (Enter)'}
                    aria-label={editingId ? 'Save message' : 'Send message'}
                    className={`w-10 h-10 rounded-xl grid place-items-center transition-all shadow-sm
                      ${sending || (!text.trim() && files.length === 0)
                        ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                        : 'bg-gradient-to-br from-brand-500 to-indigo-600 text-white hover:from-brand-600 hover:to-indigo-700 hover:shadow-md active:scale-95'}
                    `}
                  >
                    {sending ? (
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    ) : editingId ? (
                      <Check size={16} />
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <Lightbox
          items={lightbox.items}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

function FilePreview({ entry, onRemove }) {
  const { file, kind, previewUrl } = entry;
  return (
    <div className="relative w-24 h-24 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 overflow-hidden group">
      {kind === 'image' && previewUrl && <img src={previewUrl} alt="" className="w-full h-full object-cover" />}
      {kind === 'video' && previewUrl && (
        <>
          <video src={previewUrl} className="w-full h-full object-cover" muted playsInline />
          <div className="absolute inset-0 grid place-items-center bg-black/30 text-white">
            <Film size={20} />
          </div>
        </>
      )}
      {kind === 'audio' && (
        <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
          <Music size={20} />
          <span className="text-[10px] mt-1 truncate w-20 px-1 text-center">{file.name}</span>
        </div>
      )}
      {kind === 'file' && (
        <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
          <FileText size={20} />
          <span className="text-[10px] mt-1 truncate w-20 px-1 text-center">{file.name}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-slate-900/70 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition"
        title="Remove"
      >
        <X size={11} />
      </button>
      <div className="absolute bottom-0 inset-x-0 px-1 py-0.5 text-[10px] bg-gradient-to-t from-black/60 to-transparent text-white truncate">
        {bytesToHuman(file.size)}
      </div>
    </div>
  );
}

function ReplyBanner({ msg, onClear }) {
  const preview = (normalizeText(msg.text || '') || `${msg.attachments?.length || 0} attachment(s)`).slice(0, 140);
  return (
    <div className="px-4 md:px-6 pt-3">
      <div className="flex items-start justify-between gap-2 px-3 py-2 rounded-lg bg-brand-50 dark:bg-brand-500/10 border-l-4 border-brand-500">
        <div className="min-w-0">
          <div className="text-[11px] font-medium text-brand-700 dark:text-brand-300 flex items-center gap-1">
            <Reply size={11} /> Replying to {msg.sender?.name}
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300 truncate mt-0.5">{preview}</div>
        </div>
        <button type="button" onClick={onClear} className="text-slate-500 hover:text-rose-500 p-1">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function ChatBubble({ msg, own, currentUserId, onDelete, onEdit, onReply, onOpenLightbox, navigate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const mentionedMe = useMemo(
    () => (msg.mentions || []).some((u) => String(u._id || u) === String(currentUserId)),
    [msg.mentions, currentUserId]
  );

  const parts = useMemo(
    () => renderTokens(msg.text, msg.mentions || [], msg.taskRefs || [], navigate, currentUserId, own),
    [msg.text, msg.mentions, msg.taskRefs, currentUserId, own, navigate]
  );

  const attachments = msg.attachments || [];
  const images = attachments.filter((a) => a.kind === 'image');
  const videos = attachments.filter((a) => a.kind === 'video');
  const otherFiles = attachments.filter((a) => a.kind !== 'image' && a.kind !== 'video');

  return (
    <div className={`group flex items-end gap-2 ${own ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className={`w-8 h-8 shrink-0 rounded-full grid place-items-center text-white text-xs font-semibold ${
        own ? 'bg-gradient-to-br from-brand-500 to-indigo-600' : 'bg-gradient-to-br from-slate-500 to-slate-700'
      }`}>
        {msg.sender?.name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className={`max-w-[75%] ${own ? 'items-end' : 'items-start'} flex flex-col relative`}>
        {!own && <div className="text-[11px] text-slate-500 mb-0.5 ml-2">{msg.sender?.name}</div>}

        <div className={`relative ${own ? 'ml-auto' : ''}`}>
          <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
            own
              ? 'bg-brand-600 text-white rounded-br-sm'
              : mentionedMe
              ? 'bg-amber-50 dark:bg-amber-500/10 text-slate-800 dark:text-slate-100 border border-amber-200 dark:border-amber-500/30 rounded-bl-sm'
              : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-bl-sm'
          }`}>
            {msg.replyTo && (
              <div className={`mb-1.5 px-2 py-1.5 rounded-lg border-l-2 text-xs ${
                own ? 'bg-white/15 border-white/50' : 'bg-slate-100 dark:bg-slate-700/50 border-slate-400 dark:border-slate-500'
              }`}>
                <div className={`font-semibold ${own ? 'text-white/90' : 'text-slate-700 dark:text-slate-200'}`}>
                  {msg.replyTo.sender?.name}
                </div>
                <div className={`truncate ${own ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}`}>
                  {normalizeText(msg.replyTo.text || '') || `${msg.replyTo.attachments?.length || 0} attachment(s)`}
                </div>
              </div>
            )}

            {images.length > 0 && <ImageGrid images={images} onOpen={(i) => onOpenLightbox(attachments.indexOf(images[i]))} own={own} />}
            {videos.length > 0 && (
              <div className={`mt-1.5 flex flex-col gap-1.5 ${images.length === 0 ? '' : 'mt-2'}`}>
                {videos.map((v, i) => (
                  <button key={i} type="button" onClick={() => onOpenLightbox(attachments.indexOf(v))}
                    className="relative block max-w-xs rounded-lg overflow-hidden bg-black">
                    <video src={fileUrl(v.url)} className="w-full max-h-64" muted playsInline preload="metadata" />
                    <span className="absolute inset-0 grid place-items-center bg-black/30 hover:bg-black/40 transition">
                      <span className="w-12 h-12 rounded-full bg-white/90 text-slate-900 grid place-items-center">
                        <Film size={20} />
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {otherFiles.length > 0 && (
              <div className={`flex flex-col gap-1.5 ${(images.length || videos.length || msg.text) ? 'mt-2' : ''}`}>
                {otherFiles.map((f, i) => <FileRow key={i} att={f} own={own} />)}
              </div>
            )}

            {msg.text && (attachments.length === 0 ? null : <div className="mt-2" />)}
            {msg.text && <div>{parts}</div>}
          </div>

          {/* 3-dot menu */}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className={`absolute -top-2 ${own ? '-left-7' : '-right-7'} w-7 h-7 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 opacity-0 group-hover:opacity-100 transition grid place-items-center`}
            title="More"
          >
            <MoreHorizontal size={14} />
          </button>

          {menuOpen && (
            <div ref={menuRef} className={`absolute z-20 ${own ? 'right-0' : 'left-0'} top-full mt-1 w-40 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-soft py-1`}>
              <button onClick={() => { setMenuOpen(false); onReply(); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                <Reply size={14} /> Reply
              </button>
              {own && (
                <button onClick={() => { setMenuOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                  <Pencil size={14} /> Edit
                </button>
              )}
              {own && (
                <button onClick={() => { setMenuOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 flex items-center gap-2">
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          )}
        </div>

        <div className="text-[10px] text-slate-400 mt-0.5 px-1 flex items-center gap-1">
          {fmtDateTime(msg.createdAt)}
          {msg.editedAt && <span className="italic">(edited)</span>}
        </div>
      </div>
    </div>
  );
}

function ImageGrid({ images, onOpen, own }) {
  const count = images.length;
  if (count === 1) {
    return (
      <button onClick={() => onOpen(0)} className="block mt-1 mb-1 rounded-lg overflow-hidden">
        <img src={fileUrl(images[0].url)} alt={images[0].filename} className="max-w-xs max-h-72 object-cover rounded-lg" />
      </button>
    );
  }
  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-1 mt-1 mb-1 max-w-xs">
        {images.map((img, i) => (
          <button key={i} onClick={() => onOpen(i)} className="rounded-lg overflow-hidden aspect-square bg-slate-100 dark:bg-slate-700">
            <img src={fileUrl(img.url)} alt={img.filename} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    );
  }
  // 3+ : show first 2 with a +N overlay on the second
  const extra = count - 2;
  return (
    <div className="grid grid-cols-2 gap-1 mt-1 mb-1 max-w-xs">
      <button onClick={() => onOpen(0)} className="rounded-lg overflow-hidden aspect-square bg-slate-100 dark:bg-slate-700">
        <img src={fileUrl(images[0].url)} alt={images[0].filename} className="w-full h-full object-cover" />
      </button>
      <button onClick={() => onOpen(1)} className="relative rounded-lg overflow-hidden aspect-square bg-slate-100 dark:bg-slate-700">
        <img src={fileUrl(images[1].url)} alt={images[1].filename} className="w-full h-full object-cover" />
        <div className={`absolute inset-0 grid place-items-center text-white text-2xl font-bold ${own ? 'bg-black/45' : 'bg-black/45'}`}>
          +{extra}
        </div>
      </button>
    </div>
  );
}

function FileRow({ att, own }) {
  return (
    <a
      href={fileUrl(att.url)}
      download={att.filename}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg max-w-xs ${
        own
          ? 'bg-white/15 hover:bg-white/25 text-white'
          : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-100'
      }`}
    >
      <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
        own ? 'bg-white/20' : 'bg-white dark:bg-slate-800 text-slate-500'
      }`}>
        {att.kind === 'audio' ? <Music size={16} /> : <FileText size={16} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{att.filename}</div>
        <div className={`text-[11px] ${own ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}`}>
          {bytesToHuman(att.size)}
        </div>
      </div>
      <Download size={14} className="shrink-0" />
    </a>
  );
}

function Lightbox({ items, index: initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const current = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && index < items.length - 1) setIndex(index + 1);
      if (e.key === 'ArrowLeft' && index > 0) setIndex(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 grid place-items-center p-4 animate-fade-in"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
        title="Close (Esc)"
      >
        <X size={20} />
      </button>

      {items.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIndex(Math.max(0, index - 1)); }}
            disabled={index === 0}
            className="absolute left-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center disabled:opacity-30"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIndex(Math.min(items.length - 1, index + 1)); }}
            disabled={index === items.length - 1}
            className="absolute right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center disabled:opacity-30"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
            {index + 1} / {items.length}
          </div>
        </>
      )}

      <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        {current.kind === 'image' ? (
          <img src={fileUrl(current.url)} alt={current.filename} className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg" />
        ) : (
          <video src={fileUrl(current.url)} controls autoPlay className="max-w-[90vw] max-h-[85vh] rounded-lg bg-black" />
        )}
      </div>

      <a
        href={fileUrl(current.url)}
        download={current.filename}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-4 right-16 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-1.5"
      >
        <Download size={14} /> Download
      </a>
    </div>
  );
}

function renderTokens(text, mentions, taskRefs, navigate, currentUserId, ownBubble) {
  const clean = normalizeText(text);
  const needles = [
    ...mentions.map((u) => ({ str: `@${u.name}`, kind: 'user', item: u })),
    ...taskRefs.map((t) => ({ str: `#${t.title}`, kind: 'task', item: t })),
  ].sort((a, b) => b.str.length - a.str.length);
  const matches = [];
  const claimed = new Array(clean.length).fill(false);
  for (const n of needles) {
    let i = 0;
    while ((i = clean.indexOf(n.str, i)) !== -1) {
      let conflict = false;
      for (let k = i; k < i + n.str.length; k++) if (claimed[k]) { conflict = true; break; }
      if (!conflict) {
        for (let k = i; k < i + n.str.length; k++) claimed[k] = true;
        matches.push({ idx: i, len: n.str.length, ...n });
      }
      i += n.str.length;
    }
  }
  matches.sort((a, b) => a.idx - b.idx);
  const out = [];
  let cursor = 0;
  let key = 0;
  const linkClass = ownBubble
    ? 'underline decoration-white/60 text-white hover:decoration-white'
    : 'underline text-brand-600 dark:text-brand-300 hover:text-brand-700 dark:hover:text-brand-200 break-all';
  for (const m of matches) {
    if (m.idx > cursor) pushLinkified(out, clean.slice(cursor, m.idx), `seg-${key++}`, linkClass);
    if (m.kind === 'user') {
      const u = m.item;
      const isSelf = String(u._id || u) === String(currentUserId);
      out.push(
        <span
          key={`m-${key++}`}
          className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${
            ownBubble
              ? 'bg-white/20 text-white'
              : isSelf
              ? 'bg-amber-200 dark:bg-amber-500/30 text-amber-900 dark:text-amber-200'
              : 'bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300'
          }`}
          title={u?.email}
        >
          @{u.name}
        </span>
      );
    } else {
      const t = m.item;
      out.push(
        <button
          key={`t-${key++}`}
          type="button"
          onClick={() => navigate(`/tasks/${t._id || t}`)}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${
            ownBubble
              ? 'bg-white/20 text-white hover:bg-white/30'
              : 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 hover:underline'
          }`}
          title="Open task"
        >
          #{t.title}
        </button>
      );
    }
    cursor = m.idx + m.len;
  }
  if (cursor < clean.length) pushLinkified(out, clean.slice(cursor), `seg-${key++}`, linkClass);
  return out;
}
